import React, { useState, useContext, useEffect } from "react";
import { usePageTracking } from "../hooks/usePageTracking";
import { useDarkMode } from "../hooks/useDarkMode";
import { useChatCoachAI } from "../hooks/useChatCoachAI";
import { ChatContext } from "../context/ChatContext";
import { AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";

// Components
import Header from "../components/Header";
import AnimatedChatUI from "../components/AnimatedChatUI";
import WelcomeScreen from "../components/WelcomeScreen";
import FaqPanel from "../components/FaqPanel";

// Data
import { SAMPLE_QUESTIONS } from "../data/sampleQuestions";



function Home() {
  usePageTracking("ChatCoachAI");
  const { darkMode, setDarkMode } = useDarkMode();
  const { messages } = useContext(ChatContext);
  const {
    userInput,
    setUserInput,
    loading,
    streaming,
    loadingMessage,
    handleSend,
    scrollRef,
  } = useChatCoachAI();

  // State for FAQ overlay
  const [showFAQ, setShowFAQ] = useState(false);
  const [activeTab, setActiveTab] = useState("faq");

  const showChatUI = messages.length > 0;

  return (
    <div className="flex flex-col h-screen w-full text-gray-900 dark:text-gray-100 overflow-auto relative">
      {/* Background gradient */}
      <div className="background-gradient"></div>
      {/* Header */}
      <Header darkMode={darkMode} setDarkMode={setDarkMode} currentPage="home" />

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 md:px-6 lg:px-8 xl:px-10">
        {/*
          Both screens live in the same grid cell (col-start-1 row-start-1) so
          the outgoing one can overlap the incoming one while the shared
          "chatShell" layout animation runs, instead of stacking and shifting
          the page. AnimatePresence stays in its default "sync" mode for the
          same reason.
        */}
        <div className="grid w-full place-items-center">
          <AnimatePresence>
            {showChatUI ? (
              <AnimatedChatUI
                key="chatUI"
                messages={messages}
                loading={loading}
                streaming={streaming}
                loadingMessage={loadingMessage}
                userInput={userInput}
                setUserInput={setUserInput}
                handleSend={handleSend}
                scrollRef={scrollRef}
              />
            ) : (
              <WelcomeScreen
                key="initialUI"
                userInput={userInput}
                setUserInput={setUserInput}
                handleSend={handleSend}
                sampleQuestions={SAMPLE_QUESTIONS}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer with Feedback and Info buttons */}
      <div className="w-full flex justify-between items-center px-6 py-4">
        {/* Left: Feedback Button */}
        <a
          href="https://amazoninteu.qualtrics.com/jfe/form/SV_51MBlyNYtlDixH8"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-900 dark:hover:text-gray-300 hover:underline transition-colors text-sm font-medium"
          style={{
            color: darkMode ? "#ffffff" : "#000000"
          }}
        >
          Give Feedback
        </a>

        {/* Center: Disclaimer */}
        <div className="text-black-500 dark:text-white-400 text-sm text-center">
          <div className="mb-1">Optimized for Customer Service. Open to All.</div>
          <div>CoachAI can make mistakes. Always check important info.</div>
        </div>

        {/* Right: Info Button */}
        <button
          className="rounded-full p-2 w-12 h-12 flex items-center justify-center transition-shadow shadow-[0_0_10px_rgba(0,0,0,0.15)] hover:shadow-[0_0_14px_rgba(0,0,0,0.25)] dark:shadow-[0_0_10px_rgba(255,255,255,0.15)] dark:hover:shadow-[0_0_14px_rgba(255,255,255,0.25)]"
          onClick={() => setShowFAQ(!showFAQ)}
        >
          <Info className="h-8 w-8 text-gray-700 dark:text-gray-300" />
        </button>
      </div>

      {/* FAQ Panel */}
      <FaqPanel
        showFAQ={showFAQ}
        setShowFAQ={setShowFAQ}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </div>
  );
}

export default Home;
